/**
 * Spanish (es) translation.
 * Must implement EXACTLY the same shape as `pt.ts` (enforced by `Translations` type).
 */
import type { Translations } from '..';

const es: Translations = {
  meta: {
    htmlLang: 'es',
  },
  common: {
    loading: 'Cargando...',
    buy: 'Comprar',
    buyNow: 'Comprar Ahora',
    startNow: 'Empieza Ya',
    downloadApp: 'Descargar la App',
    getMyChip: 'Quiero Mi Chip',
    iWantMyChip: 'Quiero mi chip',
    close: 'Cerrar',
    cancel: 'Cancelar',
    unlimited: 'Ilimitado',
  },
  layout: {
    loading: 'Cargando...',
    errorTitle: 'Error al cargar la configuración',
  },
  topBar: {
    customerService: 'Atención',
    helpCenter: 'Centro de Ayuda',
    recharges: 'Recargas',
    portability: 'Portabilidad',
    defaultPromo: 'AHORA PUEDES VER TUS LÍNEAS EN NUESTRA NUEVA APP',
    downloadNowCta: 'DESCARGAR YA',
  },
  header: {
    home: 'Inicio',
    plans: 'Planes',
    portability: 'Portabilidad',
    advantages: 'Ventajas',
    recharge: 'Recarga',
    customerService: 'Atención',
    helpCenter: 'Centro de Ayuda',
  },
  hero: {
    defaultHeadline: 'Tú más',
    defaultHeadlineBold: 'conectado',
    defaultSubtitle: '¡La operadora que sabe cómo conectarte con el mundo!',
    defaultPlanLabel: 'PLAN PRO',
    defaultPlanData: '45',
    defaultPlanUnit: 'gb',
    defaultPlanFeatures: 'Internet + SMS + Llamadas',
    defaultPlanBenefits: 'Y 12 beneficios más increíbles',
    defaultPlanPrice: 'R$39,99',
    defaultPlanInstallment: 'o 29,90 en el plan anual',
    defaultAppCta: 'Empieza ahora mismo con la app',
    defaultCurvedText: 'Controla todo con la app White Telecom',
    defaultDisclaimer:
      'Oferta válida por tiempo limitado. Consulta condiciones, cobertura y disponibilidad en tu región. Precios sujetos a cambios sin aviso previo. Las imágenes son meramente ilustrativas.',
    defaultPromoText: 'Aprovecha nuestra promoción con +15GB',
    defaultPriceLabel: 'Solo',
    fallbackApps: 'Whatsapp Ilimitado, Waze Ilimitado',
  },
  plans: {
    tag: 'Planes y Beneficios',
    titleLine1: 'Mucho más que un plan,',
    titleLine2: 'posibilidades para hacer más',
    mostPopular: 'Más Elegido',
    appsIncluded: 'Apps Incluidas',
    perMonth: 'al mes',
  },
  benefits: {
    tag: 'Beneficios',
    titleLine1: 'Beneficios que ya vienen',
    titleLine2: 'con tu plan',
    items: [
      { title: 'Whatsapp Ilimitado', description: 'Úsalo todos los días sin descontar de tu internet.' },
      { title: 'Planes Sin Permanencia', description: 'Libertad para cancelar cuando quieras, sin multas.' },
      { title: 'Atención Que Resuelve', description: 'Soporte rápido y directo, sin vueltas.' },
      { title: 'Acumula la Internet Que No Uses', description: 'La internet que sobra vuelve a ti.' },
      { title: 'Conexión 5G Rápida', description: 'Más velocidad y estabilidad donde estés.' },
      { title: 'Experiencia 100% Digital', description: 'Resuelve todo desde la app, sin burocracia.' },
      { title: 'Chip Físico o Digital', description: 'Elige entre chip físico o eSIM.' },
      { title: 'Llamadas y SMS Ilimitados', description: 'Habla y envía mensajes sin límites.' },
    ],
  },
  features: {
    tag: 'Beneficios',
    titleLine1: 'Pensamos en cada detalle',
    titleLine2: 'para ti',
    physicalChip: 'Chip Físico',
    eSim: 'eSIM',
    chipTitle: 'Activa tu chip en minutos.',
    chipDesc: 'Elige entre chip físico o eSIM y empieza a usarlo al instante.',
    portabilityTag: 'Portabilidad',
    portabilityTitle: 'Trae tu número a {{brand}}.',
    portabilityDesc: 'Elige entre chip físico o eSIM y empieza a usarlo al instante.',
    aboutTag: 'SOBRE NOSOTROS',
    aboutTitleLine1: 'Una operadora',
    aboutTitleLine2: 'hecha para',
    aboutTitleLine3: 'simplificar tu',
    aboutTitleLine4: 'vida.',
  },
  appSection: {
    titleLine1: 'Controla todo desde la',
    titleLine2: 'aplicación.',
    appBadges: ['Saldo', 'Recargas', 'Líneas', 'Pagos', 'Portabilidad', 'Club de Descuentos'],
    activateHint: 'Descarga la app y activa tu chip en minutos.',
    socialProofTag: 'Gente',
    socialProofTitle: 'Cada vez más personas eligen {{brand}}.',
    socialProofFallbackBrand: 'nuestra operadora',
    socialProofFooter: 'Miles de personas ya están conectadas.',
    ratingSuffix: 'de valoración en la app',
    ratingFooter: 'Valoración de nuestra app en las tiendas.',
    purpleTitleLine1: 'Internet móvil sin',
    purpleTitleLine2: 'complicaciones.',
  },
  faq: {
    tag: 'Preguntas Frecuentes',
    titleLine1: '¿Tienes dudas?',
    titleLine2: 'Podemos ayudarte',
    subtitle: 'Aquí están las preguntas más frecuentes que recibimos',
    viewAnswer: 'Ver Respuesta',
    fallback: [
      { category: 'Portabilidad', question: '¿Puedo portar mi número desde cualquier operadora?', answer: '¡Sí! Puedes traer tu número desde cualquier operadora a nuestra red de forma totalmente gratuita y sin burocracia, directamente desde la app.' },
      { category: 'Cobertura', question: '¿Tienen cobertura en mi ciudad?', answer: 'Contamos con la mayor cobertura del país, alcanzando el 100% de los municipios a través de nuestra red asociada con tecnología 4G y 5G.' },
      { category: 'Portabilidad', question: '¿Puedo mantener mi número actual?', answer: '¡Por supuesto! Durante la activación de tu nuevo chip o eSIM, eliges mantener tu número y nosotros nos encargamos de toda la transición.' },
      { category: 'Planes', question: '¿Qué pasa cuando se acabe mi internet?', answer: '¡Seguirás conectado! La navegación sufre una reducción de velocidad, pero no pierdes el acceso. Si prefieres, puedes contratar paquetes adicionales al instante desde la app.' },
      { category: 'Suscripción', question: '¿Puedo cancelar en cualquier momento?', answer: 'Sí. Creemos en la libertad de nuestros clientes. Nuestros planes no tienen permanencia ni cargos ocultos por cancelación.' },
    ],
    legalTag: 'Legal',
  },
  footer: {
    support: 'Soporte',
    helpCenter: 'Centro de Ayuda',
    customerService: 'Atención',
    copyrightSuffix: 'Todos los derechos reservados',
    cnpjLabel: 'ID Fiscal',
    defaultCompany: 'White Telecom',
  },
  marquee: {
    defaultText: 'Tú mucho más conectado',
  },
  rechargeModal: {
    title: 'Hacer Recarga',
    successTitle: '¡Recarga solicitada!',
    copyLink: 'Copiar Enlace',
    payNow: 'Pagar Ahora',
    linkCopied: '¡Enlace copiado al portapapeles!',
    phoneLabel: 'Número de Teléfono',
    phonePlaceholder: '(11) 99999-9999',
    planLabel: 'Plan',
    loadingPlans: 'Cargando planes...',
    submitButton: 'Recargar',
    errorInvalidPhone: 'Ingresa un número de teléfono válido con código de área.',
    errorSelectPlan: 'Selecciona un plan.',
    errorGeneric: 'Error al procesar la recarga. Inténtalo de nuevo.',
  },
  portabilityModal: {
    title: 'Solicitar Portabilidad',
    successTitle: '¡Portabilidad solicitada!',
    protocolLabel: 'Protocolo',
    yourNumberLabel: 'Tu número en {{brand}}',
    yourNumberFallbackBrand: 'operadora',
    donorNumberLabel: 'Número a portar (operadora actual)',
    donorOperatorLabel: 'Operadora actual',
    loadingOperators: 'Cargando operadoras...',
    submitButton: 'Solicitar Portabilidad',
    errorInvalidContel: 'Ingresa un número válido con código de área.',
    errorInvalidDonor: 'Ingresa el número actual (donante) válido con código de área.',
    errorSelectOperator: 'Selecciona la operadora actual.',
    errorGeneric: 'Error al solicitar la portabilidad. Inténtalo de nuevo.',
  },
  legalModal: {
    placeholderContent: 'Contenido no configurado.',
  },
  page404: {
    title: '404',
    subtitle: 'Página no encontrada',
    back: 'Volver al inicio',
  },
  languageSwitcher: {
    label: 'Idioma',
    pt: 'Portugués',
    en: 'Inglés',
    es: 'Español',
  },
  whatsapp: {
    salesMessage: 'Me gustaría contratar un nuevo plan de {{brand}}',
    salesFallbackBrand: 'la operadora',
  },
};

export default es;
